import React, { useEffect, useState } from "react";
import Select from 'react-select';

import i18next from "i18next";
import { useTranslation } from "react-i18next";
import selectStyles from '../../../util/selectStyles';
import TabBar from '../../Messaging/RightSidebar/TabBar';
import ContentIcon from "../../icons/ContentIcon";
import HeadersIcon from "../../icons/HeadersIcon";
import KeyValuesApi from "./KeyValuesApi";
import { uuid, getOptionCollection } from './utils';
import ChatbotOptionModel from '../ChatbotOptionModel';
import IconMC from '../../../presentation/components/IconMC';

const methodOptions = [
  { value: 'post', label: i18next.t('chatbots.options.api.post') }
];

const contentOptions = [
  { value: 'form_url', label: i18next.t('chatbots.options.api.form_url') }
];

const ApiOption = ({
  chatbotOption,
  setChatbotOption,
  errors,
  customerColumns,
  setErrors
}) => {
  const [variableOptions, setVariableOptions] = useState([]);
  const { t } = useTranslation();

  const handleOnClickTest = () => {
    const {
      api_url,
      method,
      body_type,
      body,
      headers
    } = chatbotOption.api_options;

    ChatbotOptionModel.testAPI({
      api_options: {
        api_url,
        method,
        body_type,
        body,
        headers
      }
    }).then((res) => {
      let variables = [];
      let status = 'error';
      setErrors((err) => ({ ...err, api_status: '' }));

      if (res.status_code === 200 || res.status_code === 201) {
        variables = res.variables;
        status = 'success';
      }

      setChatbotOption({
        ...chatbotOption,
        api_options: {
          ...chatbotOption.api_options,
          variables
        },
        status
      });
    })
      .catch(() => {
        setChatbotOption({
          ...chatbotOption,
          api_options: {
            ...chatbotOption.api_options,
            variables: []
          },
          status: ''
        });
      });
  };

  const handleSelect = (option, field) => {
    setChatbotOption({
      ...chatbotOption,
      api_options: {
        ...chatbotOption.api_options,
        [field.name]: option.value
      },
      status: ''
    });
  };

  const handleInputChange = (e) => {
    const { target } = e;
    const { name, value } = target;

    setChatbotOption({
      ...chatbotOption,
      api_options: {
        ...chatbotOption.api_options,
        [name]: value
      }
    });
  };

  const buildFakeOptions = (currentOptions = []) => {
    const nodeId = uuid();
    const failedNodeId = uuid();

    if (currentOptions.length === 0) {
      return currentOptions.concat({
        id: null,
        text: t('chatbots.options.api.error'),
        option_deleted: false,
        node_id: failedNodeId,
        source: chatbotOption.node_id,
        target: failedNodeId,
        parent_id: chatbotOption.id,
        fake_option: true,
        option_type: 'failure'
      }).concat({
        id: null,
        text: t('chatbots.options.api.response'),
        option_deleted: false,
        node_id: nodeId,
        source: chatbotOption.node_id,
        target: nodeId,
        parent_id: chatbotOption.id,
        fake_option: true
      });
    }
    return currentOptions.map((item) => ({
      ...item,
      option_deleted: false
    }));
  };

  useEffect(() => {
    if (!chatbotOption?.id) {
      setChatbotOption({
        ...chatbotOption,
        api_options: {
          method: 'post',
          body_type: 'form_url',
          body: [],
          headers: []
        },
        options_list: buildFakeOptions(getOptionCollection(chatbotOption, 'options_list'))
      });
    } else {
      setChatbotOption({
        ...chatbotOption,
        status: 'success'
      });
    }
  }, [chatbotOption?.id]);

  useEffect(() => {
    const options = customerColumns.map((field) => ({
      value: field[1],
      label: (
        <span className="fz-14">
          {field[0]}
          {' '}
          <small className="c-grey">{`{{${field[1]}}}`}</small>
        </span>
      )
    }));

    setVariableOptions(options);
  }, [customerColumns]);

  useEffect(() => {
    let newBody;
    if (chatbotOption?.api_options?.body_type === 'form_url') {
      newBody = { key: 'Content-Type', value: 'application/x-www-form-urlencoded' };
    }

    if (newBody) {
      if (chatbotOption?.api_options?.body?.length === 0) {
        setChatbotOption({
          ...chatbotOption,
          api_options: {
            ...chatbotOption.api_options,
            headers: [newBody]
          }
        });
      } else {
        const newBodyOptions = chatbotOption?.api_options?.headers.map((item, index) => (index === 0 ? newBody : item));
        setChatbotOption({
          ...chatbotOption,
          api_options: {
            ...chatbotOption.api_options,
            headers: newBodyOptions
          },
          status: 'success'
        });
      }
    }
  }, [chatbotOption?.api_options?.body_type]);

  return (
    <div className="col-md-12 mt-10">
      <div className="figma-field">
        <label>{t('chatbots.options.api.method')}</label>
        <Select
          options={methodOptions}
          placeholder={t('chatbots.options.api.selection')}
          className="select-tag"
          classNamePrefix="select-tag"
          styles={selectStyles}
          onChange={handleSelect}
          value={[methodOptions.find((el) => el.value === chatbotOption?.api_options?.method)]}
          name="method"
          components={{
            IndicatorSeparator: () => null
          }}
        />
      </div>
      <div className="figma-field mb-24">
        <label>URL</label>
        <span className="ml-10 error-msg fz-12">{errors.api_url}</span>
        <input
          value={chatbotOption?.api_options?.api_url || ''}
          onChange={handleInputChange}
          className="input"
          placeholder={t('chatbots.options.api.urlPlaceholder')}
          name="api_url"
        />
      </div>
      <TabBar
        tabs={[
          {
            header: <TabHeader type="content" />,
            panel: (
              <>
                <div className="figma-field mt-8">
                  <label>{t('chatbots.options.api.body_type')}</label>
                  <Select
                    options={contentOptions}
                    placeholder={t('chatbots.options.api.selection')}
                    className="select-tag"
                    classNamePrefix="select-tag"
                    styles={selectStyles}
                    onChange={handleSelect}
                    value={[contentOptions.find((el) => el.value === chatbotOption?.api_options?.body_type)]}
                    name="body_type"
                    components={{
                      IndicatorSeparator: () => null
                    }}
                  />
                </div>
                <KeyValuesApi
                  data={chatbotOption?.api_options?.body}
                  setData={(data) => {
                    setChatbotOption({
                      ...chatbotOption,
                      api_options: {
                        ...chatbotOption.api_options,
                        body: data
                      }
                    });
                  }}
                  type="content"
                  variables={variableOptions}
                  errors={errors}
                />
              </>
            )
          },
          {
            header: <TabHeader type="headers" />,
            panel: (
              <KeyValuesApi
                data={chatbotOption?.api_options?.headers}
                setData={(data) => {
                  setChatbotOption({
                    ...chatbotOption,
                    api_options: {
                      ...chatbotOption.api_options,
                      headers: data
                    }
                  });
                }}
                type="headers"
                variables={variableOptions}
                errors={errors}
              />

            )
          }
        ]}
        dividerClassName=""
      />

      <div className="d-flex flex-column mt-8">
        <span className="ff-poppins-semibold fs-14 mb-8">{t('chatbots.options.api.test')}</span>
        <button type="button" className="btn border-8 c-grey-dark border-gray bg-light-gray text-center mx-0 w-max-content" onClick={handleOnClickTest}>
          {t('chatbots.options.api.testRequest')}
        </button>
      </div>

      {chatbotOption?.status && (
        <div className={`${chatbotOption.status === 'success' ? 'success-status' : 'hot-status'} p-16 border-12 d-flex align-items-center fs-12 mt-8`}>
          <IconMC name="checkmark-circle-2-outline" size="base" className="tw-mr-1 tw-flex-shrink-0" />
          {t(`chatbots.options.api.testMessage.${chatbotOption.status}`)}
        </div>
      )}
      <span className="error-msg fz-12 mt-8">{errors.api_status}</span>
    </div>
  );
};

const TabHeader = ({ type }) => (
  <div className="d-flex justify-content-center align-items-center">
    {type === 'content' ? (<ContentIcon />) : (<HeadersIcon />)}
    <span className="ml-5 fs-14">{i18next.t(`chatbots.options.api.${type}`)}</span>
  </div>
);
export default ApiOption;
